import { useNavigate } from '@tanstack/react-router';
import { useDispatch } from 'react-redux';
import { openTour } from '@/store/slices/uiSlice';
import { clearSession } from '@/lib/auth/clearSession';
import {
  MessageCircle,
  HelpCircle,
  History,
  Settings,
  Users,
  LogOut,
  Compass,
  Bug,
  ContactRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { useOnboardingStatus } from '@/api/queries/useOnboardingQueries';
import { useToast } from '@chakra-ui/react';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { railNavItems } from '@/components/nav/navItems';

export interface MenuItem {
  icon: LucideIcon;
  title: string;
  onClick: () => void;
  requiresQuiz?: boolean;
  isNativeOnly?: boolean;
  isDebug?: boolean;
  isDestructive?: boolean;
  /**
   * Route this row duplicates on the desktop rail. When the rail has an item
   * for it (`railNavItems`), the row is hidden at `lg`+ so the destination has
   * one entry point per surface; phones keep the row.
   */
  railDestination?: string;
}

// D61 Phase 6 — Quick Search retired; Menu's "FAQ" row now opens the
// marketing site's FAQ page directly rather than routing through the app.
const MARKETING_FAQ_URL = 'https://paire.io/faqs';

// The Menu's "Contact Us" row opens the marketing site's contact page (it
// replaced a mailto: draft, which does nothing useful inside a native WebView).
const MARKETING_CONTACT_URL = 'https://paire.io/contact';

export interface UseMenuItemsResult {
  /** Menu list, already filtered for native-only and desktop-rail-duplicate rows. */
  items: MenuItem[];
  needsQuiz: boolean;
  handleTakeQuizClick: () => void;
}

/**
 * Single source of truth for the Menu item list and quiz-gate banner logic
 * (D45b Phase 3). Consumed by `MenuOverlay`, the sole entry point — `/menu`
 * no longer routes to a standalone page (see `router/routes/menu.tsx`,
 * which redirects and opens `MenuOverlay` instead). The former full-screen
 * `Menu` page component that used to live in this file was dead code
 * (unreachable via routing) and was removed in the D45b review pass.
 */
export function useMenuItems(): UseMenuItemsResult {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const isNative = Capacitor.isNativePlatform();
  const isDesktop = useIsDesktop();
  const { data: onboardingStatus } = useOnboardingStatus();
  const needsQuiz = onboardingStatus?.needsQuiz ?? false;
  const nextRequiredQuizKey = onboardingStatus?.nextRequiredQuizKey ?? null;

  const handleTakeQuizClick = () => {
    if (nextRequiredQuizKey === 'level1' || !nextRequiredQuizKey) {
      navigate({ to: '/onboarding/quiz' });
    } else {
      navigate({ to: '/quizzes/$quizKey', params: { quizKey: nextRequiredQuizKey } });
    }
  };

  // Opens an external URL via the Capacitor in-app browser on native
  // platforms (window.open silently does nothing inside a native WebView),
  // falling back to window.open on web.
  const openExternalUrl = async (url: string) => {
    if (isNative) {
      try {
        await Browser.open({ url });
        return;
      } catch (err) {
        console.warn('In-app browser failed, opening externally:', err);
      }
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleLogout = async () => {
    await clearSession();
    toast({
      title: 'Logged out successfully',
      status: 'success',
      duration: 2000,
    });
    navigate({ to: '/login' });
  };

  // Same production check as `lib/landing.ts`: Vite's MODE is 'production' for
  // `vite build` (the default build mode), 'development' for `vite`, and 'test'
  // for `--mode test` builds. Read inside the hook so tests can stub it.
  const isProductionBuild = import.meta.env.MODE === 'production';

  const allItems: MenuItem[] = [
    {
      icon: Compass,
      title: 'App Tour',
      onClick: () => dispatch(openTour()),
      requiresQuiz: true,
    },
    {
      icon: History,
      title: 'History',
      onClick: () => {
        analytics.capture(AnalyticsEvents.HISTORY_CLICKED);
        navigate({ to: '/history' });
      },
      requiresQuiz: true,
      railDestination: '/history',
    },
    {
      icon: MessageCircle,
      title: 'Contact Us',
      onClick: () => {
        analytics.capture(AnalyticsEvents.FEEDBACK_CLICKED, { destination: 'contact_page' });
        void openExternalUrl(MARKETING_CONTACT_URL);
      },
    },
    {
      icon: HelpCircle,
      title: 'FAQ',
      onClick: () => {
        analytics.capture(AnalyticsEvents.FAQ_CLICKED);
        void openExternalUrl(MARKETING_FAQ_URL);
      },
    },
    {
      icon: Users,
      title: 'My Connections',
      onClick: () => navigate({ to: '/friends' }),
      requiresQuiz: true,
    },
    {
      icon: ContactRound,
      title: 'Connect with Members',
      onClick: () => navigate({ to: '/sync-contacts' }),
      isNativeOnly: true,
      requiresQuiz: true,
    },
    {
      icon: Settings,
      title: 'Settings',
      onClick: () => navigate({ to: '/settings' }),
      // /settings/* is behind the onboarding gate (canGatedMemberAccessPath),
      // so the row is locked for gated members rather than bouncing them.
      requiresQuiz: true,
    },
    {
      icon: Bug,
      title: 'Debug: Restart Onboarding',
      onClick: () => navigate({ to: '/onboarding/permissions' }),
      isDebug: true,
    },
    {
      icon: LogOut,
      title: 'Logout',
      onClick: handleLogout,
      isDestructive: true,
    },
  ];

  const items = allItems.filter(
    (item) =>
      (!item.isNativeOnly || isNative) &&
      !(item.isDebug && isProductionBuild) &&
      !(isDesktop && item.railDestination && railNavItems.some((rail) => rail.to === item.railDestination)),
  );

  return { items, needsQuiz, handleTakeQuizClick };
}
